import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { DoNotDisturb } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useApiStore, useAuthStore, useSettingsStore } from '@core/store';
import { componentService } from '@/services';
import type { ComponentData } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { ActiveLicenses } from '@core/types';
import { ComponentIcon, getComponentLabel } from '@features/administration/components/shops/ShopComponentIcons';
import { validateName, validateIpAddress, validatePort, required, requiredArray } from '@core/utils/validation';
import { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { FormContainer, WcnButton, WcnTextField } from '@/components';
import FormTextField from '@/components/form/FormTextField.tsx';
import FormSelect from '@/components/form/FormSelect.tsx';
import FormComboBox from '@/components/form/FormComboBox.tsx';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';

interface AddComponentWizardProps {
    shopId: string;
    onCancel: () => void;
    onChanged: () => void;
}

const SENSOR_CAMERA_TYPES = ['Singlesensor', 'Multisensor'];
const TX_POWER_RANGE = Array.from({ length: 21 }, (_, i) => 10 + i);
const RX_SENSITIVITY_RANGE = Array.from({ length: 81 }, (_, i) => -80 + i);

const AddComponentWizard: React.FC<AddComponentWizardProps> = ({ shopId, onCancel, onChanged }) => {
    const { t } = useTranslation();
    const { hasLicense } = useAuthStore();
    const { darkMode } = useSettingsStore();
    const { notifyWarning, notifyErrorResponse, notifySuccess } = useNotifications();
    const formRef = useRef<FormContainerRef>(null);
    const [formValid, setFormValid] = useState(false);

    const componentTypes = [
        {
            value: 'SensorCamera',
            label: getComponentLabel('SensorCamera'),
            show: hasLicense(ActiveLicenses.ShopAutomation)
        },
        {
            value: 'ImpinjRfidReader',
            label: getComponentLabel('ImpinjRfidReader'),
            show: hasLicense(ActiveLicenses.Rfid)
        },
        {
            value: 'SnmpDevice',
            label: getComponentLabel('SnmpDevice'),
            show: true
        },
        {
            value: 'MediaPlayer',
            label: getComponentLabel('MediaPlayer'),
            show: hasLicense(ActiveLicenses.ShopAutomation)
        },
        {
            value: 'SmartShelfComponent',
            label: getComponentLabel('SmartShelfComponent'),
            show: hasLicense(ActiveLicenses.SmartShelf)
        }
    ].filter((type) => type.show);

    const [activeStep, setActiveStep] = useState(0);
    const [isCreating, setIsCreating] = useState(false);

    const { gatewayIds, loadGatewayIds, loadComponentIds } = useApiStore();
    const [component, setComponent] = useState<ComponentData>({
        name: '',
        description: '',
        ipAddress: '',
        port: 0,
        discriminator: componentTypes.length > 0 ? componentTypes[0].value : '',
        shopId: shopId,
        gatewayId: null,
        lines: [],
        zones: [],
        txPowerInDbm: 20,
        rxSensitivityInDbm: -50,
        disabledAntennas: [],
        type: '',
        resetTimeoutInMs: 0,
        sensorCameraType: ''
    });

    useEffect(() => {
        loadGatewayIds();
    }, []);

    useEffect(() => {
        if (component.discriminator === 'SmartShelfComponent') {
            setComponent((prev) => ({ ...prev, port: 1883 }));
        } else if (
            component.discriminator !== 'ImpinjRfidReader' &&
            component.discriminator !== 'SensorCamera' &&
            component.discriminator !== 'SnmpDevice'
        ) {
            // Reset port for other types if it was set to 1883
            if (component.port === 1883) {
                setComponent((prev) => ({ ...prev, port: 0 }));
            }
        }
    }, [component.discriminator]);

    const shopGatewayIds = gatewayIds.filter((g) => g.shopId === shopId);

    const handleFieldChange = (fieldName: keyof ComponentData, value: any) => {
        setComponent((prev) => ({ ...prev, [fieldName]: value }));
    };

    const handleNext = () => {
        if (activeStep === 0) {
            if (!component.discriminator) {
                notifyWarning('selectComponentType');
                return;
            }
            setActiveStep(1);
        } else if (activeStep === 1) {
            const isValid = formRef.current?.validate?.() ?? formValid;
            if (!isValid) {
                notifyWarning('invalidInput');
                return;
            }
            setActiveStep(2);
        }
    };

    const handleCreate = async () => {
        const isValid = formRef.current?.validate?.() ?? formValid;
        if (!isValid) {
            notifyWarning('invalidInput');
            return;
        }

        setIsCreating(true);
        try {
            await componentService.createComponent(component);
            notifySuccess();
            await loadComponentIds();
            onChanged();
        } catch (error) {
            notifyErrorResponse(error);
        } finally {
            setIsCreating(false);
        }
    };

    const getIpAddressLabel = () => {
        return component.discriminator === 'SmartShelfComponent' ? t('smartShelfBrokerIpAddress') : t('ipAddress');
    };

    const getPortLabel = () => {
        return component.discriminator === 'SmartShelfComponent' ? t('smartShelfBrokerPort') : t('port');
    };

    return (
        <Box className="editor-container">
            <Box className="editor-sidebar-actions">
                <Button variant="text" startIcon={<DoNotDisturb />} onClick={onCancel}>
                    {t('cancel')}
                </Button>
            </Box>

            <Box className="editor-sidebar-content add-components-content">
                <Box className={`outline-box ${darkMode ? 'dark-mode' : ''}`}>
                    <Stepper
                        activeStep={activeStep}
                        orientation="vertical"
                        className={`custom-stepper ${darkMode ? 'dark-mode' : ''}`}
                    >
                        <Step>
                            <StepLabel>{t('selectComponentType')}</StepLabel>
                            <StepContent>
                                <List className="component-type-list">
                                    {componentTypes.map((type) => (
                                        <ListItem
                                            key={type.value}
                                            button
                                            selected={component.discriminator === type.value}
                                            onClick={() => handleFieldChange('discriminator', type.value)}
                                            className={`component-list-item ${darkMode ? 'dark-mode' : ''} ${component.discriminator === type.value ? 'selected' : ''}`}
                                        >
                                            <ListItemIcon>
                                                <ComponentIcon discriminator={type.value} />
                                            </ListItemIcon>
                                            <ListItemText primary={type.label} />
                                        </ListItem>
                                    ))}
                                </List>
                                <Box className="form-actions">
                                    <WcnButton variant="primary" onClick={handleNext}>
                                        {t('next')}
                                    </WcnButton>
                                </Box>
                            </StepContent>
                        </Step>

                        <Step>
                            <StepLabel>{t('fillProps')}</StepLabel>
                            <StepContent>
                                <Box className="wizard-form-container">
                                    <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                                        <div className="form-row">
                                            <div className="form-col form-col-12 form-col-6">
                                                <FormTextField
                                                    name={'name'}
                                                    label={t('name')}
                                                    value={component.name}
                                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                                    required
                                                    fullWidth
                                                    FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                    rules={[validateName(t)]}
                                                />
                                            </div>
                                        </div>

                                        <div className="form-row">
                                            <div className="form-col form-col-12 form-col-6">
                                                <WcnTextField
                                                    label={t('description')}
                                                    value={component.description}
                                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                                    fullWidth
                                                />
                                            </div>
                                        </div>

                                        {component.discriminator !== 'SnmpDevice' && (
                                            <div className="form-row">
                                                <div className="form-col form-col-12 form-col-6">
                                                    <FormTextField
                                                        name={'ipAddress'}
                                                        label={getIpAddressLabel()}
                                                        value={component.ipAddress}
                                                        onChange={(e) => handleFieldChange('ipAddress', e.target.value)}
                                                        required
                                                        fullWidth
                                                        FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                        rules={[validateIpAddress(t)]}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {['MediaPlayer', 'SmartShelfComponent'].includes(component.discriminator) && (
                                            <div className="form-row">
                                                <div className="form-col form-col-12 form-col-6">
                                                    <FormTextField
                                                        name={'port'}
                                                        label={getPortLabel()}
                                                        type="number"
                                                        value={component.port}
                                                        onChange={(e) =>
                                                            handleFieldChange('port', parseInt(e.target.value) || 0)
                                                        }
                                                        required
                                                        fullWidth
                                                        FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                        rules={[validatePort(t)]}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {component.discriminator === 'SnmpDevice' && (
                                            <div className="form-row">
                                                <div className="form-col form-col-12 form-col-6">
                                                    <FormSelect
                                                        name={'gatewayId'}
                                                        label={t('gateway')}
                                                        value={component.gatewayId || ''}
                                                        onChange={(e) => handleFieldChange('gatewayId', e)}
                                                        items={shopGatewayIds}
                                                        itemText={'deviceId'}
                                                        itemValue={'id'}
                                                        rules={[required(t)]}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {component.discriminator === 'MediaPlayer' && (
                                            <>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12 form-col-6">
                                                        <FormTextField
                                                            name={'type'}
                                                            label={t('resetTimeoutInMs')}
                                                            type="number"
                                                            value={component.resetTimeoutInMs}
                                                            onChange={(e) =>
                                                                handleFieldChange(
                                                                    'resetTimeoutInMs',
                                                                    parseInt(e.target.value) || 0
                                                                )
                                                            }
                                                            required
                                                            fullWidth
                                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                            rules={[required(t)]}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12 form-col-6">
                                                        <FormSelect
                                                            name={'gatewayId'}
                                                            label={t('gateway')}
                                                            value={component.gatewayId || ''}
                                                            onChange={(e) => handleFieldChange('gatewayId', e)}
                                                            items={shopGatewayIds}
                                                            itemText={'deviceId'}
                                                            itemValue={'id'}
                                                            rules={[required(t)]}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {component.discriminator === 'SensorCamera' && (
                                            <>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12 form-col-6">
                                                        <FormSelect
                                                            name={'sensorCameraType'}
                                                            label={t('type')}
                                                            value={component.sensorCameraType || ''}
                                                            onChange={(e) => handleFieldChange('sensorCameraType', e)}
                                                            items={SENSOR_CAMERA_TYPES.map((type) => ({ value: type }))}
                                                            itemText={'value'}
                                                            itemValue={'value'}
                                                            rules={[required(t)]}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12">
                                                        <FormComboBox
                                                            name={'lines'}
                                                            label={t('lines')}
                                                            value={component.lines || []}
                                                            onChange={(vals) => handleFieldChange('lines', vals)}
                                                            rules={[requiredArray(t)]}
                                                            freeSolo
                                                            fullWidth
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12">
                                                        <FormComboBox
                                                            name={'zones'}
                                                            label={t('zones')}
                                                            value={component.zones || []}
                                                            onChange={(vals) => handleFieldChange('zones', vals)}
                                                            freeSolo
                                                            fullWidth
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {component.discriminator === 'ImpinjRfidReader' && (
                                            <>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12 form-col-6">
                                                        <FormSelect
                                                            name={'txPowerInDbm'}
                                                            label={t('txPowerInDbm')}
                                                            value={component.txPowerInDbm || ''}
                                                            onChange={(e) => handleFieldChange('txPowerInDbm', e)}
                                                            items={TX_POWER_RANGE.map((type) => ({ value: type }))}
                                                            itemText={'value'}
                                                            itemValue={'value'}
                                                            rules={[required(t)]}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12 form-col-6">
                                                        <FormSelect
                                                            name={'rxSensitivityInDbm'}
                                                            label={t('rxSensitivityInDbm')}
                                                            value={component.rxSensitivityInDbm || ''}
                                                            onChange={(e) => handleFieldChange('rxSensitivityInDbm', e)}
                                                            items={RX_SENSITIVITY_RANGE.map((type) => ({
                                                                value: type
                                                            }))}
                                                            itemText={'value'}
                                                            itemValue={'value'}
                                                            rules={[required(t)]}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-col form-col-12">
                                                        <FormComboBox
                                                            name={'disabledAntennas'}
                                                            label={t('disabledAntennas')}
                                                            value={component.disabledAntennas || []}
                                                            onChange={(vals) =>
                                                                handleFieldChange('disabledAntennas', vals)
                                                            }
                                                            freeSolo
                                                            fullWidth
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </FormContainer>
                                </Box>
                                <Box className="form-actions">
                                    <Button onClick={() => setActiveStep(0)}>{t('back')}</Button>
                                    <WcnButton variant="primary" onClick={handleNext}>
                                        {t('next')}
                                    </WcnButton>
                                </Box>
                            </StepContent>
                        </Step>

                        <Step>
                            <StepLabel>{t('viewSummary')}</StepLabel>
                            <StepContent>
                                <Box className={`summary-box ${darkMode ? 'dark-mode' : 'light-mode'}`}>
                                    <pre>{JSON.stringify(component, null, 2)}</pre>
                                </Box>
                                <Box className="form-actions">
                                    <Button onClick={() => setActiveStep(1)}>{t('back')}</Button>
                                    <WcnButton variant="primary" onClick={handleCreate} disabled={isCreating}>
                                        {t('create')}
                                    </WcnButton>
                                </Box>
                            </StepContent>
                        </Step>
                    </Stepper>
                </Box>
            </Box>

            <LoadingOverlay open={isCreating} message={t('creating')} showMessage={true} />
        </Box>
    );
};

export default AddComponentWizard;
